import React, { useMemo } from 'react';
import styled from 'styled-components';
import { motion, useReducedMotion } from 'framer-motion';

import { founderTypes, FOUNDER_TYPE_IDS } from '../data/founderTypes';
import { formatFounderTitle } from '../utils/textUtils';
import { buildTypeCode } from '../utils/founderIdentity';

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 2rem 0;
`;

const LoadingCard = styled.div`
  width: 100%;
  max-width: 640px;
  background-color: rgba(18, 18, 28, 0.82);
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 16px;
  backdrop-filter: blur(12px);
  padding: 1.5rem;
  box-shadow: 0 16px 48px rgba(0, 0, 0, 0.25);
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: var(--preview-accent, var(--primary-color));
  }

  @media (min-width: 640px) {
    padding: 2.25rem;
  }
`;

const Title = styled.h2`
  font-family: var(--font-display);
  color: var(--text-primary);
  font-weight: 800;
  margin: 0 0 0.5rem;
  letter-spacing: -0.02em;
  font-size: 1.5rem;
  line-height: 1.2;

  @media (min-width: 640px) {
    font-size: 1.75rem;
  }
`;

const Subtitle = styled.p`
  color: var(--text-secondary);
  margin: 0 0 1.25rem;
  line-height: 1.6;
  font-size: 1.02rem;
`;

const PreviewBlock = styled.div`
  margin: 1.25rem 0 1rem;
  padding: 1rem 1.1rem;
  border-radius: 14px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
`;

const PreviewLabel = styled.p`
  margin: 0 0 0.75rem;
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
`;

const TypeRow = styled.div`
  display: flex;
  align-items: center;
  gap: 0.65rem;
  margin-bottom: 0.55rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const TypeCode = styled.span`
  font-size: 0.68rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  color: ${(props) => props.$color || 'var(--primary-color)'};
  min-width: 2rem;
`;

const TypeBarTrack = styled.div`
  flex: 1;
  height: 6px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.08);
  overflow: hidden;
`;

const TypeBarFill = styled(motion.div)`
  height: 100%;
  border-radius: 999px;
  background: ${(props) => props.$color || 'var(--primary-color)'};
`;

const TypePct = styled.span`
  font-size: 0.78rem;
  font-weight: 600;
  color: rgba(255, 255, 255, 0.55);
  min-width: 2.25rem;
  text-align: right;
  font-variant-numeric: tabular-nums;
`;

const EmergingHint = styled(motion.p)`
  margin: 0.85rem 0 0;
  font-size: 0.95rem;
  line-height: 1.5;
  color: var(--text-primary);

  strong {
    color: var(--preview-accent, var(--primary-light));
  }
`;

const StepList = styled.ol`
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.55rem;
`;

const StepItem = styled.li`
  display: flex;
  align-items: flex-start;
  gap: 0.65rem;
  font-size: 0.92rem;
  line-height: 1.45;
  color: ${(props) => (props.$done ? 'var(--text-primary)' : 'rgba(255, 255, 255, 0.45)')};
`;

const StepDot = styled.span`
  width: 18px;
  height: 18px;
  min-width: 18px;
  margin-top: 0.1rem;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 0.65rem;
  font-weight: 800;
  background: ${(props) =>
    props.$done ? 'rgba(var(--primary-rgb), 0.22)' : 'rgba(255, 255, 255, 0.06)'};
  border: 1px solid ${(props) =>
    props.$active ? 'var(--primary-color)' : props.$done ? 'rgba(var(--primary-rgb), 0.35)' : 'rgba(255, 255, 255, 0.1)'};
  color: ${(props) => (props.$done || props.$active ? 'var(--primary-light)' : 'transparent')};
`;

const ProgressWrap = styled.div`
  margin-top: 1.15rem;
`;

const ProgressRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
`;

const ProgressBar = styled.div`
  flex: 1;
  height: 6px;
  background: rgba(255, 255, 255, 0.08);
  border-radius: 999px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${(props) => `${Math.max(0, Math.min(100, props.value || 0))}%`};
  background: var(--preview-accent, var(--primary-color));
  border-radius: 999px;
  transition: width 0.35s ease;
`;

const ProgressPct = styled.div`
  color: rgba(255, 255, 255, 0.55);
  font-variant-numeric: tabular-nums;
  font-size: 0.85rem;
  font-weight: 600;
  min-width: 3rem;
  text-align: right;
`;

const DEFAULT_STEPS = [
  'Reading your decision patterns',
  'Scoring your founder modes',
  'Finding your primary type',
  'Building your identity profile',
];

const DnaLoadingAnimation = ({
  title = 'Calculating your founder type',
  subtitle = 'Mapping how you lead, decide, and execute under pressure.',
  progress = 0,
  steps = DEFAULT_STEPS,
  profilePreview = null,
}) => {
  const reduceMotion = useReducedMotion();
  const safeProgress = Math.max(0, Math.min(100, progress));

  const activeStepIndex = useMemo(() => {
    if (!Array.isArray(steps) || steps.length === 0) return 0;
    return Math.min(steps.length - 1, Math.floor((safeProgress / 100) * steps.length));
  }, [safeProgress, steps]);

  const topTypes = useMemo(() => {
    if (!profilePreview?.percentages) return [];
    return FOUNDER_TYPE_IDS
      .map((id) => ({
        id,
        pct: profilePreview.percentages[id] || 0,
        meta: founderTypes[id],
      }))
      .filter((row) => row.pct > 0)
      .sort((a, b) => b.pct - a.pct)
      .slice(0, 3);
  }, [profilePreview]);

  const primaryType = profilePreview?.primaryType ? founderTypes[profilePreview.primaryType] : null;
  const secondaryType = profilePreview?.secondaryType ? founderTypes[profilePreview.secondaryType] : null;
  const previewAccent = primaryType?.color || 'var(--primary-color)';
  const revealIdentity = safeProgress >= 55 && primaryType;
  const revealBlend = safeProgress >= 78 && secondaryType;

  return (
    <LoadingContainer>
      <LoadingCard style={{ '--preview-accent': previewAccent }}>
        <Title>{title}</Title>
        <Subtitle>{subtitle}</Subtitle>

        {topTypes.length > 0 && (
          <PreviewBlock aria-live="polite">
            <PreviewLabel>Live type signal</PreviewLabel>
            {topTypes.map((row, index) => (
              <TypeRow key={row.id}>
                <TypeCode $color={row.meta?.color}>{row.meta?.code}</TypeCode>
                <TypeBarTrack>
                  <TypeBarFill
                    $color={row.meta?.color}
                    initial={false}
                    animate={{
                      width: `${Math.round((safeProgress / 100) * row.pct)}%`,
                    }}
                    transition={reduceMotion ? { duration: 0 } : { duration: 0.35, ease: 'easeOut' }}
                  />
                </TypeBarTrack>
                <TypePct>{row.pct}%</TypePct>
              </TypeRow>
            ))}

            {revealIdentity && (
              <EmergingHint
                initial={reduceMotion ? false : { opacity: 0, y: 6 }}
                animate={{ opacity: 1, y: 0 }}
              >
                Emerging type: <strong>{formatFounderTitle(primaryType.title)}</strong>
                {revealBlend && (
                  <>
                    {' '}
                    with <strong>{formatFounderTitle(secondaryType.title)}</strong> influence
                    {' '}
                    ({buildTypeCode(profilePreview.primaryType, profilePreview.secondaryType)})
                  </>
                )}
              </EmergingHint>
            )}
          </PreviewBlock>
        )}

        <StepList aria-label="Analysis steps">
          {steps.map((step, index) => {
            const done = index < activeStepIndex;
            const active = index === activeStepIndex;
            return (
              <StepItem key={step} $done={done || active}>
                <StepDot $done={done} $active={active} aria-hidden="true">
                  {done ? '✓' : active ? '•' : ''}
                </StepDot>
                <span>{step}</span>
              </StepItem>
            );
          })}
        </StepList>

        <ProgressWrap aria-label="Analysis progress">
          <ProgressRow>
            <ProgressBar>
              <ProgressFill value={safeProgress} />
            </ProgressBar>
            <ProgressPct>{Math.round(safeProgress)}%</ProgressPct>
          </ProgressRow>
        </ProgressWrap>
      </LoadingCard>
    </LoadingContainer>
  );
};

export default DnaLoadingAnimation;
